import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { Actions, ofActionCompleted, ofActionDispatched, Select, Store } from '@ngxs/store';
import { GradingSchemeState } from 'app/library/grading-scheme/models/grading-scheme.state';
import {
    FetchModuleFinalGrades,
    FetchModuleFinalGradesFailure,
    FetchModuleFinalGradesForParticipant,
    FetchModuleFinalGradesForParticipantFailure,
    FetchModuleFinalGradesForParticipantSuccess,
    FetchModuleFinalGradesSuccess,
    SearchModuleFinalGrades,
} from 'app/library/module-final-grades/actions';
import { ModuleFinalGradesState } from 'app/library/module-final-grades/models/module-final-grades.state';
import { SnackBarTime } from 'app/projects/core/src/lib/constants/snack-bar';
import { NgxsActionHelper } from 'app/projects/core/src/lib/services/action.helper';
import { PageSizePreferenceService } from 'app/projects/shared/src/lib/services/page-size-preference.service';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import {
    FetchFinalGrade,
    FetchFinalGradeFailure,
    FetchFinalGradesForCurrentUser,
    FetchFinalGradeSuccess,
    FetchGradesForParticipant,
    FetchGradesForParticipantFailure,
    FetchGradesForParticipantSuccess,
    FetchParticipantFinalGrade,
    UpdateFinalGrade,
    UpdateFinalGradeFailure,
    UpdateFinalGradeSuccess,
} from '../actions';
import {
    BulkUpdateFinalGrades,
    BulkUpdateFinalGradesFailure,
    BulkUpdateFinalGradesStatus,
    BulkUpdateFinalGradesStatusFailure,
    BulkUpdateFinalGradesStatusSuccess,
    BulkUpdateFinalGradesSuccess,
    FetchFinalGradesForCurrentUserFailure,
    FetchFinalGradesForCurrentUserSuccess,
} from '../actions/index';
import { FinalGradeExtended } from '../models/final-grade-extended.model';
import { FinalGradeCommand } from '../models/final-grade.command.model';
import { FinalGrade } from '../models/final-grade.model';
import { FinalGradeState } from '../models/final-grade.state';

@Injectable()
export class FinalGradeService {
    @Select(ModuleFinalGradesState.getEntities)
    moduleFinalGrade$: Observable<FinalGrade[]>;

    @Select(FinalGradeState.getEntity)
    currentFinalGrade$: Observable<FinalGrade>;

    get currentFinalGradeSnapshot() {
        return this._store.selectSnapshot(FinalGradeState.getEntity);
    }

    constructor(
        private _store: Store,
        private _action$: Actions,
        private _actionHelper: NgxsActionHelper,
        private _translateService: TranslateService,
        private _matSnackBar: MatSnackBar,
        private _pageSizePreferenceService: PageSizePreferenceService
    ) {}

    getFinalGradesOfCurrentModule(withUser: boolean = false): FinalGrade[] {
        if (withUser) {
            return this._store.selectSnapshot(ModuleFinalGradesState.getEntitiesWithUser);
        }

        return this._store.selectSnapshot(ModuleFinalGradesState.getEntities);
    }

    getModuleFinalGradesStateProperty(property: string): string {
        return this._store.selectSnapshot(ModuleFinalGradesState)[property];
    }

    getFilteredFinalGradesOfCurrentModule(withUser: boolean = false): FinalGrade[] {
        if (withUser) {
            return this._store.selectSnapshot(ModuleFinalGradesState.getFilteredEntitiesWithUser);
        }

        return this._store.selectSnapshot(ModuleFinalGradesState.getEntities);
    }

    getCurrentFinalGrade(): FinalGrade {
        return this._store.selectSnapshot(FinalGradeState.getEntity);
    }

    getFinalGradeById(id: string): FinalGrade {
        return FinalGradeState.getEntity(this._store.selectSnapshot(FinalGradeState), this._store.selectSnapshot(GradingSchemeState.getEntity), id);
    }

    getUserFinalGrades(): FinalGradeExtended[] {
        return this._store.selectSnapshot(FinalGradeState.getUserEntities);
    }

    async fetchFinalGradeOfCurrentUserByModuleId(moduleId: string): Promise<boolean> {
        this._store.dispatch(new FetchParticipantFinalGrade(moduleId));

        return await this._actionHelper.race(FetchFinalGradeSuccess, FetchFinalGradeFailure).toPromise();
    }

    async fetchFinalGradeOfCurrentUser(): Promise<boolean> {
        this._store.dispatch(new FetchFinalGradesForCurrentUser());

        return await this._actionHelper.race(FetchFinalGradesForCurrentUserSuccess, FetchFinalGradesForCurrentUserFailure).toPromise();
    }

    async fetchParticipantGradesByModuleId(
        moduleId: string,
        search?: string,
        sortBy?: string,
        sortOrder?: string,
        pageSize = this._pageSizePreferenceService.getPageSize('module-form-gradebook-tab-grades', [10, 25, 50], 10),
        pageNumber?: number
    ): Promise<boolean> {
        this._store.dispatch(new FetchModuleFinalGrades({ moduleId, search, sortBy, sortOrder, pageSize, pageNumber }));

        return await this._actionHelper.race(FetchModuleFinalGradesSuccess, FetchModuleFinalGradesFailure).toPromise();
    }

    async fetchGradesForParticipant(moduleId: string): Promise<boolean> {
        this._store.dispatch(new FetchGradesForParticipant({ moduleId }));

        return await this._actionHelper.race(FetchGradesForParticipantSuccess, FetchGradesForParticipantFailure).toPromise();
    }

    async fetchParticipantFinalGradesByModuleIdForParticipant(moduleId: string, participantId: string): Promise<boolean> {
        this._store.dispatch(new FetchModuleFinalGradesForParticipant({ moduleId, participantId }));

        return this._actionHelper.race(FetchModuleFinalGradesForParticipantSuccess, FetchModuleFinalGradesForParticipantFailure).toPromise();
    }

    async fetchFinalGradeById(finalGradeId: string, force: boolean = false): Promise<boolean> {
        if (!force) {
            const finalGrade = this.getCurrentFinalGrade();

            if (finalGrade && finalGrade.id === finalGradeId) {
                return true;
            }
        }

        this._store.dispatch(new FetchFinalGrade(finalGradeId));

        return await this._actionHelper.race(FetchFinalGradeSuccess, FetchFinalGradeFailure).toPromise();
    }

    async updateFinalGrade(finalGrade: FinalGrade): Promise<FinalGrade> {
        this._store.dispatch(new UpdateFinalGrade(finalGrade));

        const updateFinalGradeResponseAction = await this._action$.pipe(ofActionDispatched(UpdateFinalGradeSuccess, UpdateFinalGradeFailure), take(1)).toPromise();

        if (updateFinalGradeResponseAction instanceof UpdateFinalGradeSuccess) {
            const successMessage = this._translateService.instant('FINAL_GRADE.MESSAGE.CHANGES_SAVED');

            this._matSnackBar.open(successMessage, 'OK', {
                verticalPosition: 'top',
                duration: SnackBarTime.medium,
            });

            return updateFinalGradeResponseAction.payload;
        }

        if (updateFinalGradeResponseAction instanceof UpdateFinalGradeFailure) {
            const failureMessage = this._translateService.instant(`ERROR.RESPONSE.${updateFinalGradeResponseAction.payload.response.error.message}`);

            this._matSnackBar.open(failureMessage, 'OK', {
                verticalPosition: 'top',
                duration: SnackBarTime.medium,
            });
        }

        return null;
    }

    async bulkUpdateFinalGrades(finalGrades: FinalGradeCommand[]): Promise<boolean> {
        this._store.dispatch(new BulkUpdateFinalGrades(finalGrades));

        return await this._actionHelper.race(BulkUpdateFinalGradesSuccess, BulkUpdateFinalGradesFailure).toPromise();
    }

    async bulkUpdateFinalGradesStatus(partialGradeIds: string[], status: string): Promise<boolean> {
        this._store.dispatch(
            new BulkUpdateFinalGradesStatus({
                entities: partialGradeIds,
                status,
            })
        );

        return await this._actionHelper.race(BulkUpdateFinalGradesStatusSuccess, BulkUpdateFinalGradesStatusFailure).toPromise();
    }

    async filterFinalGrades(searchTerm: string): Promise<boolean> {
        setTimeout(() => {
            this._store.dispatch(new SearchModuleFinalGrades(searchTerm));
        });

        return await this._action$
            .pipe(
                ofActionCompleted(SearchModuleFinalGrades),
                map(({ result }) => result.successful),
                take(1)
            )
            .toPromise();
    }
}
